import { TranslocoService } from '@jsverse/transloco';
import { combineLatest, map, Observable } from 'rxjs';

import { DeviceUiInputsDeviceBehaviorPlugin } from '@shure/device-ui/inputs/data-access';
import { IntellimixType } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiIntellimixDeviceBehaviorPlugin } from '@shure/device-ui/intellimix/data-access';
import { DeviceUiOutputsDeviceBehaviorPlugin } from '@shure/device-ui/outputs/data-access';
import {
	DeviceUiSchematicsDeviceBehaviorPlugin,
	SchematicsDeviceChannelDspKind,
	SchematicsDeviceChannel
} from '@shure/device-ui/schematics/data-access';
import {
	DeviceUiSettingsFactoryResetBehaviorPlugin,
	FactoryResetText
} from '@shure/device-ui/settings/factory-reset/data-access';
import { DeviceUiSettingsGeneralBehaviorPlugin } from '@shure/device-ui/settings/general/data-access';
import { DspChannelType, DspCopyChannelProperty, DspType } from '@shure/device-ui/shared/dsp/data-access';
import { DeviceUiPage, DeviceUiRoute, DeviceUiSharedDeviceBehaviorPlugin } from '@shure/device-ui/shared/services';
import { DeviceUiShellDeviceBehaviorPlugin } from '@shure/device-ui/shell/data-access';
import { ChannelGroup, DeviceModel } from '@shure/shared/angular/data-access/system-api/models';
import { DeviceBehaviorPlugin, DeviceNameBehaviorPlugin } from '@shure/shared/angular/device-behavior/core';
import {
	LightsSettingsDeviceBehaviorPlugin,
	LightsSettingsSupportType
} from '@shure/shared/angular/features/device-lights-settings/data-access';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

@shUseTranslation('scDevUiSharedDevBehavior', (lang) => import(`../i18n/${lang}`))
export class P300DeviceUiDeviceBehaviorPlugin
	implements
		DeviceBehaviorPlugin,
		DeviceUiShellDeviceBehaviorPlugin,
		DeviceUiSchematicsDeviceBehaviorPlugin,
		LightsSettingsDeviceBehaviorPlugin,
		DeviceUiOutputsDeviceBehaviorPlugin,
		DeviceUiSettingsGeneralBehaviorPlugin,
		DeviceUiSettingsFactoryResetBehaviorPlugin,
		DeviceUiInputsDeviceBehaviorPlugin,
		DeviceUiIntellimixDeviceBehaviorPlugin,
		DeviceNameBehaviorPlugin,
		DeviceUiSharedDeviceBehaviorPlugin
{
	public readonly models: DeviceModel[] = [DeviceModel.P300];

	constructor(protected readonly transloco: TranslocoService) {}

	public getLightsSettingsSupportType(): LightsSettingsSupportType {
		return LightsSettingsSupportType.SupportedWithoutImage;
	}

	public getInputChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [
			ChannelGroup.DanteInput,
			ChannelGroup.DanteAuxInput,
			ChannelGroup.AnalogAuxInput,
			ChannelGroup.UsbInput,
			ChannelGroup.MobileInput
		];
	}

	public getOutputChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.MixOutput, ChannelGroup.AuxOutput, ChannelGroup.UsbOutput, ChannelGroup.MobileOutput];
	}

	public getAutomixedChannelGroups(_model: DeviceModel): ChannelGroup[] {
		return [ChannelGroup.DanteInput];
	}

	public isCompressorCopyPasteSupported(_model: DeviceModel): boolean {
		return false;
	}

	public getAvailablePages$(
		_model: DeviceModel,
		_options?: { virtual?: boolean | undefined; autoCoverageMode?: boolean | undefined } | undefined
	): Observable<DeviceUiPage[] | undefined> {
		return combineLatest([
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.schematic'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.inputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.automixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.matrix-mixer'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.outputs'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.presets'),
			this.transloco.selectTranslate('scDevUiSharedDevBehavior.toolbar.labels.settings')
		]).pipe(
			map(
				([
					schematicLabel,
					inputsLabel,
					autoMixerLabel,
					matrixMixerLabel,
					outputsLabel,
					presetsLabel,
					settingsLabel
				]) => [
					{
						route: DeviceUiRoute.Schematic,
						label: schematicLabel
					},
					{
						route: DeviceUiRoute.Inputs,
						label: inputsLabel
					},
					{
						route: DeviceUiRoute.Intellimix,
						label: autoMixerLabel
					},
					{
						route: DeviceUiRoute.Matrixmixer,
						label: matrixMixerLabel
					},
					{
						route: DeviceUiRoute.Outputs,
						label: outputsLabel
					},
					{
						route: DeviceUiRoute.Presets,
						label: presetsLabel
					},
					{
						route: DeviceUiRoute.Settings,
						label: settingsLabel
					}
				]
			)
		);
	}
	public getDefaultRoute(_model: DeviceModel): DeviceUiRoute | undefined {
		return DeviceUiRoute.Schematic;
	}

	public getResetText(_model: DeviceModel): FactoryResetText {
		return {
			tab$: this.transloco.selectTranslate('scDevUiSharedDevBehavior.settings.factory-reset'),
			title: this.transloco.translate('scDevUiSharedDevBehavior.settings.factory-reset'),
			button: this.transloco.translate(
				'scDevUiSharedDevBehavior.settings.factory-reset-page.restore-factory-defaults'
			)
		};
	}

	public showPatentInfo(_model: DeviceModel): boolean {
		return false;
	}

	public getOutputChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Delay, 2]
		]);
	}

	public getInputChannelDspSortingMap(_model: DeviceModel): Map<DspType, number> {
		return new Map<DspType, number>([
			[DspType.Peq, 1],
			[DspType.Eq, 2],
			[DspType.Aec, 3],
			[DspType.Nr, 4],
			[DspType.Agc, 5]
		]);
	}

	public getIntellimixType(_model: DeviceModel): IntellimixType | undefined {
		return IntellimixType.Basic;
	}

	public revertAutomixerDefaultsCapability(): boolean {
		return true;
	}

	public getDeviceInputChannelsSortingMap(_model: DeviceModel): Map<ChannelGroup, number> | undefined {
		return new Map<ChannelGroup, number>([
			[ChannelGroup.DanteInput, 0],
			[ChannelGroup.Automix, 1],
			[ChannelGroup.DanteAuxInput, 2],
			[ChannelGroup.AnalogAuxInput, 3],
			[ChannelGroup.DanteAnalogInput, 4],
			[ChannelGroup.UsbInput, 5],
			[ChannelGroup.MobileInput, 6]
		]);
	}

	public getSchematicOutputChannelDspSortingMap(_model: DeviceModel): Map<SchematicsDeviceChannelDspKind, number> {
		return new Map<SchematicsDeviceChannelDspKind, number>([
			['equalizer', 1],
			['delay', 2]
		]);
	}

	public getChannelCopyPasteProperties(
		_model: DeviceModel,
		_channelType: DspChannelType,
		_selectedChannels: SchematicsDeviceChannel[]
	): DspCopyChannelProperty[] {
		return ['muted', 'gain', 'analogLevel', 'autoMute'];
	}

	public getDeviceNameMaxLength(_model: DeviceModel): number | undefined {
		return 31;
	}

	public displayAutomixerDirectOutTapPoint(_model: DeviceModel): boolean {
		return true;
	}

	public getDirectChannelGroups(_model: DeviceModel): undefined {
		return undefined;
	}

	public shouldResetOuputGroupIndex(_model: DeviceModel): boolean {
		return false;
	}
}
